import type { ErrorEvent, EventHint } from '@sentry/react';
import { AxiosError } from 'axios';
import { AjaxError } from 'rxjs/ajax';
import {
  DecodingError,
  HttpError,
  RequestError,
} from '@rossum/api-client/errors';
import type { EventCategory } from './policy';
import { getApiErrorStatusCode, getApiErrorUrl } from './utils';

/**
 * Messages emitted via `captureMessage` that we treat as no-ops at the
 * reporting layer. We keep the call sites where the surrounding logic
 * still wants a Sentry tag/extras, and rely on the classifier to drop them.
 */
const INFO_NOISE_MESSAGES: ReadonlySet<string> = new Set([
  'Assets Mismatch Detected',
]);

/**
 * Heuristic for the "Failed to fetch" errors that originate from a lazy
 * chunk reload helper after a deploy. Sentry's built-in chunk-load inbound
 * filter doesn't match our bundler wrapper, so we catch it here.
 *
 * Identified in Sentry as a `TypeError: Failed to fetch` whose stack
 * references a `reload_helper` frame.
 */
const isChunkReloadFailure = (error: unknown): boolean => {
  if (!(error instanceof TypeError)) return false;
  if (error.message !== 'Failed to fetch') return false;
  return typeof error.stack === 'string' && /reload_helper/.test(error.stack);
};

const isHttpish = (
  error: unknown
): error is HttpError | AjaxError | AxiosError =>
  error instanceof HttpError ||
  error instanceof AjaxError ||
  error instanceof AxiosError;

/**
 * 400s on auth/oauth endpoints are user-driven (bad credentials, expired refresh
 * tokens) and not actionable from the FE. Previously suppressed explicitly by
 * `isIgnoredApiEndpoint`; preserved here so the new pipeline matches old behaviour.
 */
const NOISY_API_4XX_URL_PATTERNS = [/\/auth\//, /\/oauth\//] as const;

const isAuthOrOAuth400 = (
  error: HttpError | AjaxError | AxiosError,
  code: number
): boolean => {
  if (code !== 400) return false;
  const url = getApiErrorUrl(error);
  return (
    typeof url === 'string' &&
    NOISY_API_4XX_URL_PATTERNS.some(pattern => pattern.test(url))
  );
};

/**
 * `/annotations/search` returns 400 "Invalid hyperlink - Object does not exist."
 * when a referenced object was deleted between the search request being
 * constructed and being sent. Benign — the user just retries.
 */
const isSearchInvalidHyperlink400 = (error: unknown): boolean => {
  if (!(error instanceof HttpError)) return false;
  if (error.code !== 400) return false;
  if (error.endpoint !== '/annotations/search') return false;
  return /Invalid hyperlink - Object does not exist\./.test(
    JSON.stringify(error.data ?? {})
  );
};

export const classifyEvent = (
  event: ErrorEvent,
  hint: EventHint
): EventCategory => {
  const error = hint.originalException;

  if (error instanceof DecodingError) return 'decoding-error';

  if (error instanceof RequestError) return 'api-network';

  if (isHttpish(error)) {
    const code = getApiErrorStatusCode(error);
    if (code === null) return 'api-network';
    if (code >= 500) return 'api-5xx';
    if (code >= 400) {
      // Develop reports only 400 among 4xx; all other 4xx (401/403/404/422/…)
      // are user/permission-driven and not actionable from the FE.
      if (code !== 400) return 'api-4xx-other';
      if (isAuthOrOAuth400(error, code) || isSearchInvalidHyperlink400(error)) {
        return 'api-4xx-noise';
      }
      return 'api-4xx';
    }
    return 'api-network';
  }

  if (isChunkReloadFailure(error)) return 'api-network';

  const message = event.message ?? '';
  if (message === 'CSP Violation Detected') return 'csp-violation';
  if (INFO_NOISE_MESSAGES.has(message)) return 'info-noise';

  return 'default';
};
